/**
 * Admin — Upload log page.
 *
 * Read-only audit of all files uploaded to the platform.
 * Filterable by user_id and category.
 */
import { useState, useEffect } from 'react';
import { Card, Select, Input, Space, Tag, Statistic, Row, Col } from 'antd';
import { DataTable } from '../../components/DataTable';
import { api } from '../../api/client';

const CATEGORY_COLORS: Record<string, string> = {
  avatar: 'blue',
  kyc_front: 'purple',
  kyc_back: 'purple',
  kyc_selfie: 'purple',
  recharge_proof: 'green',
  other: 'default',
};

const CATEGORY_LABELS: Record<string, string> = {
  avatar: '头像',
  kyc_front: 'KYC正面',
  kyc_back: 'KYC反面',
  kyc_selfie: 'KYC自拍',
  recharge_proof: '充值凭证',
  other: '其他',
};

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export function UploadLogPage() {
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [userIdFilter, setUserIdFilter] = useState<number | undefined>(undefined);
  const [stats, setStats] = useState<{ total_uploads: number; total_size_bytes: number } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res: any = await api.get('/api/admin/uploads/stats');
        setStats(res);
      } catch {
        // ignore
      }
    })();
  }, []);

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 80,
      render: (v: number | null) => v ?? '-' },
    { title: '管理员ID', dataIndex: 'admin_id', key: 'admin_id', width: 80,
      render: (v: number | null) => v ?? '-' },
    { title: '原始文件名', dataIndex: 'original_name', key: 'original_name', width: 180,
      ellipsis: true },
    { title: 'MIME', dataIndex: 'mime_type', key: 'mime_type', width: 120 },
    { title: '大小', dataIndex: 'size_bytes', key: 'size_bytes', width: 90,
      render: (v: number) => formatBytes(v) },
    {
      title: '分类', dataIndex: 'category', key: 'category', width: 100,
      render: (v: string) => <Tag color={CATEGORY_COLORS[v] ?? 'default'}>{CATEGORY_LABELS[v] || v}</Tag>,
    },
    { title: 'URL', dataIndex: 'url', key: 'url', width: 200, ellipsis: true },
    {
      title: '上传时间', dataIndex: 'create_time', key: 'create_time', width: 170,
      render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-',
    },
  ];

  return (
    <div>
      {stats && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Card size="small">
              <Statistic title="总上传数" value={stats.total_uploads} />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic title="总大小" value={formatBytes(Number(stats.total_size_bytes))} />
            </Card>
          </Col>
        </Row>
      )}

      <Space style={{ marginBottom: 16 }}>
        <Select
          style={{ width: 160 }}
          placeholder="筛选分类"
          allowClear
          value={categoryFilter}
          onChange={(v) => setCategoryFilter(v)}
          options={[
            { value: 'avatar', label: '头像' },
            { value: 'kyc_front', label: 'KYC正面' },
            { value: 'kyc_back', label: 'KYC反面' },
            { value: 'kyc_selfie', label: 'KYC自拍' },
            { value: 'recharge_proof', label: '充值凭证' },
            { value: 'other', label: '其他' },
          ]}
        />
        <Input
          style={{ width: 120 }}
          placeholder="用户ID"
          type="number"
          value={userIdFilter ?? ''}
          onChange={(e) => {
            const v = e.target.value ? Number(e.target.value) : undefined;
            setUserIdFilter(v);
          }}
          allowClear
        />
      </Space>

      <DataTable<any>
        queryKey={['upload-log']}
        endpoint="/api/admin/uploads"
        extraParams={{
          ...(categoryFilter ? { category: categoryFilter } : {}),
          ...(userIdFilter ? { user_id: userIdFilter } : {}),
        }}
        columns={columns}
        rowKey="id"
      />
    </div>
  );
}

export default UploadLogPage;
